import React, { useLayoutEffect } from 'react';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../../../util/reactIntl';
import { pathByRouteName } from '../../../../util/routes';
import { useRouteConfiguration } from '../../../../context/routeConfigurationContext';
import { xenforoPublicOrigin } from '../../../../util/xenforoUrls';
import { ExternalLink, NamedLink } from '../../../../components';

import ForumMessagesLink from '../ForumMessagesLink/ForumMessagesLink';
import {
  getActiveMobileBottomTab,
  MOBILE_BOTTOM_NAV_BODY_CLASS,
  shouldShowMobileBottomNav,
  subscribeMobileBottomNavKeyboard,
} from './mobileBottomNavHelpers';
import {
  BrowseIcon,
  CommunityIcon,
  ListingsIcon,
  PlusIcon,
  TransactionsIcon,
} from './MobileBottomNavIcons';

import css from './MobileBottomNav.module.css';

/**
 * Floating mobile bottom tab bar (Browse / Listings / + / Transactions / Community).
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.isAuthenticated
 * @param {string?} props.currentPage
 * @param {number} props.notificationCount
 * @param {number} [props.forumUnreadCount]
 * @param {string} props.inboxTab sales | purchases | orders
 * @returns {JSX.Element|null}
 */
const MobileBottomNav = props => {
  const {
    isAuthenticated,
    currentPage,
    notificationCount = 0,
    forumUnreadCount = 0,
    inboxTab,
  } = props;
  const intl = useIntl();
  const routeConfiguration = useRouteConfiguration();

  const show = shouldShowMobileBottomNav(currentPage);
  const activeTab = getActiveMobileBottomTab(currentPage);
  const communityHref = xenforoPublicOrigin();

  // useLayoutEffect: apply clearance before paint to avoid content sitting under the bar.
  useLayoutEffect(() => {
    if (typeof document === 'undefined') {
      return undefined;
    }
    const { body } = document;
    if (show) {
      body.classList.add(MOBILE_BOTTOM_NAV_BODY_CLASS);
    } else {
      body.classList.remove(MOBILE_BOTTOM_NAV_BODY_CLASS);
    }
    return () => {
      body.classList.remove(MOBILE_BOTTOM_NAV_BODY_CLASS);
    };
  }, [show]);

  // Hide the floating bar while the software keyboard is open (iOS pushes
  // position:fixed up with the visual viewport, covering the field).
  useLayoutEffect(() => {
    if (!show) {
      return undefined;
    }
    return subscribeMobileBottomNavKeyboard();
  }, [show]);

  if (!show) {
    return null;
  }

  const listingsFrom = pathByRouteName('ManageListingsPage', routeConfiguration);
  const newListingFrom = pathByRouteName('NewListingPage', routeConfiguration);
  const transactionsFrom = pathByRouteName('TransactionsPage', routeConfiguration, {
    tab: inboxTab,
  });

  const showBadge = notificationCount > 0;
  const badgeLabel = intl.formatMessage(
    { id: 'MobileBottomNav.notifications' },
    { count: notificationCount }
  );

  const listingsLink = isAuthenticated ? (
    <NamedLink
      name="ManageListingsPage"
      className={classNames(css.tab, { [css.tabActive]: activeTab === 'listings' })}
      title={intl.formatMessage({ id: 'MobileBottomNav.listings' })}
      aria-current={activeTab === 'listings' ? 'page' : undefined}
    >
      <span className={css.tabInner}>
        <span className={css.iconWrap}>
          <ListingsIcon />
        </span>
        <span className={css.visuallyHidden}>
          <FormattedMessage id="MobileBottomNav.listings" />
        </span>
      </span>
    </NamedLink>
  ) : (
    <NamedLink
      name="LoginPage"
      to={{ state: { from: listingsFrom } }}
      className={css.tab}
      title={intl.formatMessage({ id: 'MobileBottomNav.listings' })}
    >
      <span className={css.tabInner}>
        <span className={css.iconWrap}>
          <ListingsIcon />
        </span>
        <span className={css.visuallyHidden}>
          <FormattedMessage id="MobileBottomNav.listings" />
        </span>
      </span>
    </NamedLink>
  );

  const fabLink = isAuthenticated ? (
    <NamedLink
      name="NewListingPage"
      className={css.fab}
      title={intl.formatMessage({ id: 'MobileBottomNav.post' })}
    >
      <PlusIcon />
      <span className={css.visuallyHidden}>
        <FormattedMessage id="MobileBottomNav.post" />
      </span>
    </NamedLink>
  ) : (
    <NamedLink
      name="LoginPage"
      to={{ state: { from: newListingFrom } }}
      className={css.fab}
      title={intl.formatMessage({ id: 'MobileBottomNav.post' })}
    >
      <PlusIcon />
      <span className={css.visuallyHidden}>
        <FormattedMessage id="MobileBottomNav.post" />
      </span>
    </NamedLink>
  );

  const transactionsLink = isAuthenticated ? (
    <NamedLink
      name="TransactionsPage"
      params={{ tab: inboxTab }}
      className={classNames(css.tab, { [css.tabActive]: activeTab === 'transactions' })}
      title={intl.formatMessage({ id: 'MobileBottomNav.transactions' })}
      aria-current={activeTab === 'transactions' ? 'page' : undefined}
    >
      <span className={css.tabInner}>
        <span className={css.iconWrap}>
          <TransactionsIcon />
          {showBadge ? (
            <span className={css.badge} aria-label={badgeLabel}>
              {notificationCount > 99 ? '99+' : notificationCount}
            </span>
          ) : null}
        </span>
        <span className={css.visuallyHidden}>
          <FormattedMessage id="MobileBottomNav.transactions" />
        </span>
      </span>
    </NamedLink>
  ) : (
    <NamedLink
      name="LoginPage"
      to={{ state: { from: transactionsFrom } }}
      className={css.tab}
      title={intl.formatMessage({ id: 'MobileBottomNav.transactions' })}
    >
      <span className={css.tabInner}>
        <span className={css.iconWrap}>
          <TransactionsIcon />
        </span>
        <span className={css.visuallyHidden}>
          <FormattedMessage id="MobileBottomNav.transactions" />
        </span>
      </span>
    </NamedLink>
  );

  return (
    <nav
      className={css.root}
      aria-label={intl.formatMessage({ id: 'MobileBottomNav.screenreader.nav' })}
    >
      <div className={css.bar}>
        <NamedLink
          name="SearchPage"
          className={classNames(css.tab, { [css.tabActive]: activeTab === 'browse' })}
          title={intl.formatMessage({ id: 'MobileBottomNav.browse' })}
          aria-current={activeTab === 'browse' ? 'page' : undefined}
        >
          <span className={css.tabInner}>
            <span className={css.iconWrap}>
              <BrowseIcon />
            </span>
            <span className={css.visuallyHidden}>
              <FormattedMessage id="MobileBottomNav.browse" />
            </span>
          </span>
        </NamedLink>

        {listingsLink}

        <div className={css.fabSlot} aria-hidden="true" />
        {fabLink}

        {transactionsLink}

        {isAuthenticated ? (
          <ForumMessagesLink
            className={css.tab}
            iconWrapClassName={css.iconWrap}
            badgeClassName={css.badge}
            unreadCount={forumUnreadCount}
            iconSize={35}
          >
            <span className={css.visuallyHidden}>
              <FormattedMessage id="MobileBottomNav.messages" />
            </span>
          </ForumMessagesLink>
        ) : (
          <ExternalLink
            href={communityHref}
            className={css.tab}
            title={intl.formatMessage({ id: 'MobileBottomNav.community' })}
          >
            <span className={css.tabInner}>
              <span className={css.iconWrap}>
                <CommunityIcon />
              </span>
              <span className={css.visuallyHidden}>
                <FormattedMessage id="MobileBottomNav.community" />
              </span>
            </span>
          </ExternalLink>
        )}
      </div>
    </nav>
  );
};

export default MobileBottomNav;
